// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The PRIMARY-GATE table: bottom-up reconstruction vs FE actuals, per level, with the
// residual against the tolerance and a pass flag. This is what approve_reporting_gate's
// reconciliation.all_pass condition is derived from — the reviewer sees every residual.

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import type { Reconciliation } from "@/types/ir";
import { CellValue } from "./CellValue";
import { EmptyState } from "@/components/research-hub";

export function ReconciliationTable({ reconciliation }: { reconciliation: Reconciliation | undefined }) {
  const levels = reconciliation?.levels ?? {};
  const keys = Object.keys(levels);
  if (keys.length === 0) return <EmptyState title="No reconciliation rows in this cycle yet." />;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-body text-xs text-muted-foreground">
          Tolerance ±{reconciliation?.tolerance_pp ?? "—"}pp · reconstruction vs FE actual
        </p>
        <span className="inline-flex items-center gap-1.5 font-body text-xs">
          {reconciliation?.all_pass ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />
              <span className="text-primary">All levels pass</span>
            </>
          ) : (
            <>
              <XCircle className="h-3.5 w-3.5 text-destructive" aria-hidden />
              <span className="text-destructive">Reconciliation NOT clean</span>
            </>
          )}
        </span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Level</TableHead>
              <TableHead className="text-right">Reconstructed</TableHead>
              <TableHead className="text-right">FE actual</TableHead>
              <TableHead className="text-right">Residual</TableHead>
              <TableHead className="text-right">Pass</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((k) => {
              const lvl = levels[k];
              return (
                <TableRow key={k}>
                  <TableCell className="font-body font-medium">{k}</TableCell>
                  <TableCell className="text-right"><CellValue cell={lvl.reconstructed} /></TableCell>
                  <TableCell className="text-right"><CellValue cell={lvl.actual} /></TableCell>
                  <TableCell className="text-right"><CellValue cell={lvl.residual} /></TableCell>
                  <TableCell className="text-right">
                    {/* An UN-RUN level (evaluated:false / pass:null) must never render as a
                        measured failure — that would read as "reconciled and broke tolerance"
                        when in truth the ±tolerance comparison never happened. Three states,
                        not two. It still blocks the gate (all_pass requires evaluated AND pass). */}
                    {lvl.evaluated === false || lvl.pass === null || lvl.pass === undefined ? (
                      <MinusCircle
                        className="h-4 w-4 text-muted-foreground inline"
                        aria-label="not evaluated"
                      />
                    ) : lvl.pass ? (
                      <CheckCircle2 className="h-4 w-4 text-primary inline" aria-label="pass" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive inline" aria-label="fail" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
